import { Injectable } from "@angular/core";
import { Action, NgxsOnInit, Selector, State, StateContext } from "@ngxs/store";
import { JwtAuthService } from "app/auth/services/jwt-auth.service";
import { GetToken, Login, LoginFailed, LoginOauthSuccess, LoginSuccess, Logout, PageRefreshTokenRefresh } from "./auth.action";
import { AuthStateModel } from "./auth.model";
import { map, filter, catchError, takeUntil } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";
import { environment } from "environments/environment";
import jwt_decode from 'jwt-decode';
import { timer, of, Subject } from 'rxjs';
import { IdentityAuthService } from "app/auth/services/identity-auth.service";

@State<AuthStateModel>({
  name: "auth",
  defaults: {
    isLoggedIn: false,
    isLoggedOut: false,
    loginData: null,
    error: "",
  },
})
@Injectable()
export class AuthState implements NgxsOnInit {
  isRefresh: boolean = false;
  private cancelTimer$ = new Subject<void>();

  @Selector()
  static loggedInUser(state: AuthStateModel): any | null {
    return state.loginData;
  }

  constructor(
    private jwtService: JwtAuthService,
    private translate: TranslateService,
    private identityAuthService: IdentityAuthService,
  ) { }

  ngxsOnInit(ctx: StateContext<AuthStateModel>) {

  }

  @Action(Login)
  login(ctx: StateContext<AuthStateModel>, action: Login) {
    return this.jwtService.Login(action).pipe(
      map((data) => {
        if (!!!data) {
          throw new Error(this.translate.instant('error.ErrLogin'));
        }
        this.isRefresh = true;
        ctx.setState(this.setLoggedInUser(data));
        ctx.dispatch(new LoginSuccess(data, this.isRefresh));
      }),
      catchError((ex) => {
        if (ex && ex.message)
          console.error(ex.message);
        if (environment.winAuth)
          return of(ctx.dispatch(new GetToken()));

        return of(ctx.dispatch(new LoginFailed(ex.message)));
      })
    )
  }

  @Action(LoginOauthSuccess)
  loginOauthSuccess(ctx: StateContext<AuthStateModel>, action: LoginOauthSuccess) {
    ctx.setState(this.setLoggedInUser(action.data));
    ctx.dispatch(new LoginSuccess(action.data, action.isRefresh));
  }

  @Action(LoginSuccess)
  loginSuccessAction(ctx: StateContext<AuthStateModel>, action: LoginSuccess) {
    this.cancelTimer$.next();
    let period = environment.tokenExpired;

    if (!!action.data) {
      const decoded = jwt_decode(action.data.token) as any;

      if (decoded && decoded.exp) {
        const now = Math.floor(Date.now() / 1000);
        period = Math.max(0, (decoded.exp - now - 10) * 1000);
      }
    }

    this.isRefresh = action.isRefresh;

    return timer(period).pipe(
      takeUntil(this.cancelTimer$),
      filter(() => this.isRefresh),
      map(() => ctx.dispatch(new GetToken())))
  }

  @Action(GetToken)
  getToken(ctx: StateContext<AuthStateModel>, action: GetToken) {
    // Pre-flight: provera da li je neki drugi tab refresh-ovao token, pre request-a ka UK backend-u
    if (this.isRefresh && this.jwtService.isTokenValid()) {
      const rawToken = this.jwtService.getJwtToken();
      const decoded = jwt_decode(rawToken) as any;
      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp - now > 30) {
        const user = this.jwtService.getUser();
        if (user) {
          this.isRefresh = true;
          return of(ctx.dispatch(new LoginSuccess(user, true)));
        }
      }
    }

    let obs = !this.isRefresh ? this.jwtService.Login(null) : this.jwtService.RefreshToken();

    return obs
      .pipe(
        map((data) => {
          if (!!!data) {
            throw new Error(this.translate.instant('error.ErrLogin'));
          }
          this.isRefresh = true;
          return ctx.dispatch(new LoginSuccess(data, this.isRefresh));
        }),
        catchError((ex) => {
          if (ex && ex.message)
            console.error(ex.message);

          // Post-failure: provera da li je neki drugi tab refresh-ovao token, tokom request-a ka UK backend-u
          // koji je rezultirao u gresci (Invalid refresh token)
          if (this.jwtService.isTokenValid()) {
            const user = this.jwtService.getUser();
            if (user) {
              this.isRefresh = true;
              return of(ctx.dispatch(new LoginSuccess(user, true)));
            }
          }

          if (environment.winAuth)
            return of(ctx.dispatch(new GetToken()));

          return of(ctx.dispatch(new LoginFailed(ex.message)));
        })
      )
  }

  @Action(Logout)
  logout(ctx: StateContext<AuthStateModel>, action: Logout) {
    this.isRefresh = false;

    if (!!environment.enableSSO) {
      if (this.identityAuthService.isLoggedIn()) {
        this.identityAuthService.logOut(action.noRedirectToUrl);
      }
    }
  }

  @Action(PageRefreshTokenRefresh)
  tokenRefreshOnPageRefresh(ctx: StateContext<AuthStateModel>, action: PageRefreshTokenRefresh) {
    this.isRefresh = true;
    this.cancelTimer$.next();

    if (!this.jwtService.isLoggedIn()) {
      return;
    }

    if (!this.jwtService.isTokenValid()) {
      return ctx.dispatch(new GetToken());
    }

    const rawToken = this.jwtService.getJwtToken();
    const decoded = jwt_decode(rawToken) as any;
    const now = Math.floor(Date.now() / 1000);
    const remainingMs = Math.max(0, (decoded.exp - now - 10) * 1000);

    return timer(remainingMs).pipe(
      takeUntil(this.cancelTimer$),
      filter(() => this.isRefresh),
      map(() => ctx.dispatch(new GetToken()))
    );
  }

  setLoggedInUser(loginData: any) {
    return (state: AuthStateModel) => ({
      ...state,
      loginData: loginData,
    });
  }
}
